import type { ComponentData, Data } from '@puckeditor/core'
import { BreakpointId, isOverrideBreakpoint } from './breakpoints'
import type { PropOverrides, ResponsiveData } from './types'
import { DISPLAY_NONE, getId, isComponentArray } from './_tree'

export interface ResolveOptions {
  /**
   * Drop nodes whose resolved `display === 'none'` (and their subtree).
   * `true` for production render; `false` for the editor canvas — where pruning
   * would lose the node from the tree on the next onChange/fold (data loss).
   */
  prune?: boolean
}

/**
 * Resolve a ResponsiveData down to a plain Puck `Data` for one breakpoint:
 *   1. merge `responsive[bp].props[id]` over each component's props,
 *   2. recurse into slots/zones,
 *   3. (when `prune`) drop any node whose resolved `display === 'none'`.
 *
 * `default` (or a missing layer) returns the base tree (still pruning base
 * `display: none` when pruning). Pure; memoized per (data, breakpoint, prune).
 */
const cache = new WeakMap<ResponsiveData, Map<string, Data>>()

export function resolveResponsive(
  data: ResponsiveData,
  bp: BreakpointId,
  opts: ResolveOptions = {},
): Data {
  const prune = opts.prune ?? true
  const key = `${bp}|${prune}`

  let perKey = cache.get(data)
  if (!perKey) {
    perKey = new Map()
    cache.set(data, perKey)
  }
  const cached = perKey.get(key)
  if (cached) return cached

  const { responsive, ...base } = data
  const overrides: Record<string, PropOverrides> =
    (isOverrideBreakpoint(bp) ? responsive?.[bp]?.props : undefined) ?? {}

  const resolved: Data = {
    ...base,
    content: resolveNodes(base.content ?? [], overrides, prune),
    ...(base.zones
      ? {
          zones: Object.fromEntries(
            Object.entries(base.zones).map(([zone, nodes]) => [
              zone,
              resolveNodes(nodes, overrides, prune),
            ]),
          ),
        }
      : {}),
  }

  perKey.set(key, resolved)
  return resolved
}

function resolveNodes(
  nodes: ComponentData[],
  overrides: Record<string, PropOverrides>,
  prune: boolean,
): ComponentData[] {
  const out: ComponentData[] = []
  for (const node of nodes) {
    const resolved = resolveNode(node, overrides, prune)
    if (resolved) out.push(resolved)
  }
  return out
}

function resolveNode(
  node: ComponentData,
  overrides: Record<string, PropOverrides>,
  prune: boolean,
): ComponentData | null {
  const id = getId(node)
  const override = id ? overrides[id] : undefined
  const props = override ? { ...node.props, ...override } : node.props

  // prune hidden nodes (and their whole subtree) — production only
  if (prune && (props as { display?: string })?.display === DISPLAY_NONE) {
    return null
  }

  // recurse into slot props
  let nextProps = props
  for (const key of Object.keys(props ?? {})) {
    const value = (props as Record<string, unknown>)[key]
    if (isComponentArray(value)) {
      if (nextProps === props) nextProps = { ...props }
      ;(nextProps as Record<string, unknown>)[key] = resolveNodes(
        value,
        overrides,
        prune,
      )
    }
  }

  return nextProps === node.props ? node : { ...node, props: nextProps }
}
